import "./App.css";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
// import { Formik } from "formik";
import Login from "./screens/auth/Login";
import Signup from "./screens/auth/Signup";
import Dashboard from "./screens/Dashboard/Dashboard";
import ContractorProfile from "./screens/Dashboard/Contractors/ContractorProfile";
import OtpScreen from "./screens/auth/OtpScreen";
import Profession from "./screens/auth/Profession";
import UserDetails from "./screens/auth/UserDetails";
import UserAddress from "./screens/auth/UserAddress";
import Sitehome from "./screens/Project Dashboard/SiteHome";
import WelcomePage from "./screens/auth/Welcome";
import AddYourOrganization from "./screens/Dashboard/AddOrganization";
import OrganizationDetailsForm from "./screens/Dashboard/AddOrganizationDetails";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import OrganizationDetails from "screens/Dashboard/OrganizationOwner";
import TaskDetails from "./screens/Project Dashboard/Tasks/TaskFormDetails";
import { AlertProvider } from "components/UI_components/Alert";
import SubscriptionModal from "./screens/Dashboard/Subscription/SubscriptionModal";
import { ForgotPassword } from "screens/auth/ResetPasswordRequest";
import { VerifyOTP } from "screens/auth/VerifyOTPReset";
import { NewPassword } from "screens/auth/NewPassword";
import MobileRedirectNotice from "components/MobileRedirectNotice";
import NetworkStatus from "components/UI_components/NetworkStatus"; // Import the new NetworkStatus component
import SnagsChat from "./screens/Project Dashboard/Snags/ChatWindow";
import OrganizationTeam from "./screens/Dashboard/Organization Team/OrganizationTeam";
import IndividualAccess from "./screens/Dashboard/Organization Team/IndividualAccess";
import { rtdb } from "utils/firebase";
import { useScreenTimeFirebase } from "utils/hooks/useScreenTimeFirebase";
import { useEffect, useState } from "react";
import { environment } from "utils/constant";
import NotificationPopup from "components/NotificationPopup";
import { NotificationProvider } from "utils/Notifications/NotificationContext";
import { set } from "date-fns";
import Subscription from "screens/Dashboard/Subscription/Subscription";
import QuotationForm from "screens/Quotations";
import { getFinanceCategories } from "utils/getFinanceCategories";
import { Toaster } from "sonner";
const queryClient = new QueryClient();

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const isAuthenticated = localStorage.getItem("access");
  const isOwner = localStorage.getItem("isOwner") === "true";
  const location = useLocation();

  // Routes restricted to owners only
  const ownerOnlyRoutes = ["/dashboard", "/dashboard/home"];

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Block non-owners from owner-only routes
  const normalizedPath =
    location.pathname.endsWith("/") && location.pathname.length > 1
      ? location.pathname.slice(0, -1)
      : location.pathname;

  if (!isOwner && ownerOnlyRoutes.includes(normalizedPath)) {
    return <Navigate to="/dashboard/projects" replace />;
  }

  return children;
};

function App() {
  const [userId, setUserId] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [isOwner, setIsOwner] = useState(false);
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    setIsOwner(user.profession === "owner");
    const organization = JSON.parse(
      localStorage.getItem("organization") || "{}"
    );
    if (user) {
      setUserId(user.user);
    }
    if (organization) {
      setOrganization(organization);
    }
  }, [localStorage.getItem("user"), localStorage.getItem("organization")]);

  useEffect(() => {
    const accessToken = localStorage.getItem("access");
    if (!accessToken) return;
    getFinanceCategories();
  }, [localStorage.getItem("access")]);

  const { active, todayMs, flush, prepareForLogout } = useScreenTimeFirebase({
    rtdb,
    userId,
    pathBase: `screenTime-${environment}`,
    idleTimeoutMs: 60_000,
    flushIntervalMs: 60_000,
  });

  return (
    <>
      <MobileRedirectNotice />
      <NetworkStatus /> {/* Add the NetworkStatus component here */}
      <Toaster position="top-right" richColors />
      <NotificationProvider
        organizationId={organization?.id}
        userId={userId}
        isOwner={isOwner}
      >
        <NotificationPopup />
        <AlertProvider>
          <QueryClientProvider client={queryClient}>
            <Router>
              <Routes>
                <Route exact path="/subtask" element={<TaskDetails />} />
                <Route exact path="/login" element={<Login />} />
                <Route exact path="/signup" element={<Signup />} />
                <Route exact path="/otp-screen" element={<OtpScreen />} />
                <Route
                  exact
                  path="/quotations/:token"
                  element={<QuotationForm />}
                />
                <Route
                  exact
                  path="/forgot-password"
                  element={<ForgotPassword />}
                />
                <Route exact path="/verify-otp" element={<VerifyOTP />} />
                <Route exact path="/new-password" element={<NewPassword />} />
                <Route
                  exact
                  path="/"
                  element={<Navigate to="/login" replace />}
                />
                <Route
                  exact
                  path="/subscription"
                  element={<Subscription prepareForLogout={prepareForLogout} />}
                />

                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <Dashboard
                        active={active}
                        flush={flush}
                        todayMs={todayMs}
                        prepareForLogout={prepareForLogout}
                      />
                    </ProtectedRoute>
                  }
                >
                  {/* Nested routes for dashboard tabs */}
                  <Route
                    index
                    element={<Navigate to="/dashboard/home" replace />}
                  />
                  <Route path="home" element={null} />
                  <Route path="projects" element={null} />
                  <Route path="procurement" element={null} />
                  <Route
                    path="central-inventory"
                    element={<Navigate to="/dashboard/procurement" replace />}
                  />
                  <Route path="vendor" element={null} />
                  <Route path="contractor" element={null} />
                  <Route
                    path="contractor/:id"
                    element={<ContractorProfile />}
                  />
                  <Route path="finance" element={null} />
                  <Route path="organization-team" element={null} />
                  <Route path="quotation-generator" element={null} />
                  <Route path="global-communication" element={null} />
                  <Route path="help" element={null} />
                  <Route path="raise-request" element={null} />
                </Route>
                <Route exact path="/profession" element={<Profession />} />
                <Route path="/userdetails" element={<UserDetails />} />
                <Route path="/useraddress" element={<UserAddress />} />
                {/* <Route path="/sitehome" element={<Sitehome />} /> */}
                <Route
                  path="/site/:siteId/*"
                  element={
                    <ProtectedRoute>
                      <Sitehome prepareForLogout={prepareForLogout} />
                    </ProtectedRoute>
                  }
                />
                <Route path="/welcome" element={<WelcomePage />} />
                <Route
                  exact
                  path="/subscription"
                  element={
                    <SubscriptionModal
                      onClose={() => {
                        // console.log("close");
                      }}
                      isOpen={true}
                    />
                  }
                />
                <Route
                  path="/add-organization"
                  element={<AddYourOrganization />}
                />
                <Route
                  exact
                  path="/organization-details"
                  element={<OrganizationDetailsForm />}
                />
                {/* <Route
                exact
                path="/chat/:id"
                element={
                  <ProtectedRoute>
                    <SnagsChat />
                  </ProtectedRoute>
                }
              /> */}
                <Route
                  exact
                  path="/organization/team"
                  element={
                    <ProtectedRoute>
                      <OrganizationTeam />
                    </ProtectedRoute>
                  }
                />
                <Route
                  exact
                  path="/organization/team/:memberId"
                  element={
                    <ProtectedRoute>
                      <IndividualAccess />
                    </ProtectedRoute>
                  }
                />
              </Routes>
            </Router>
          </QueryClientProvider>
        </AlertProvider>
      </NotificationProvider>
    </>
  );
}

export default App;
